import React, { useCallback, useMemo } from 'react';

import {
  EmailMessageEnvironmentFilter,
  EmailMessageEventTypeFilter,
  type EmailMessagesFilterInput,
  EmailMessageTimePeriodFilter,
  type FEmailMessageLightFragment,
  useEmailMessagesQuery,
} from '@dashboard/generated/wp/platform';
import usePlatformNavigator from '@dashboard/hooks/navigate/usePlatformNavigator';
import { Paper, Stack } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import useQueryDataTableProps from '@wp-react/hooks/useQueryDataTableProps';

import Header from '../pages/QueryListPage/PageWrapper/Header';
import { WpDataGrid } from '../pages/QueryListPage/TableList/WpDataGrid';
import TableLoader from '../table/TableLoader';

import EmptyResultsMessage from './EmptyResultsMessage';
import FiltersBar from './FiltersBar';
import TableTitleBar from './TableTitleBar';
import TitleBar from './TitleBar';

export type TColumns = {
  headerName: GridColDef<FEmailMessageLightFragment>['headerName'];
  minWidth?: number;
  maxWidth?: number;
  renderCell: GridColDef<FEmailMessageLightFragment>['renderCell'];
}[];

type Props = {
  title: JsxNode;
  page?: number;

  columns: TColumns;

  emptyMessageTutorial?: JsxNode;
  filter: EmailMessagesFilterInput;
  setFilter: (value: EmailMessagesFilterInput) => void;
};

export default function EmailMessagesList({ title, page, emptyMessageTutorial, filter, setFilter, columns }: Props) {
  const calculatedColumns = useMemo(
    () =>
      columns.map(({ minWidth, ...colProps }, index) => ({
        ...colProps,
        field: `field-${index}`,
        sortable: false,
        minWidth: minWidth ?? 160,
        flex: 1,
        type: 'string',
      })),
    [columns]
  );
  const { data } = useEmailMessagesQuery({ fetchPolicy: 'network-only', variables: { page, filter } });
  const navigator = usePlatformNavigator();
  const { pagination } = useQueryDataTableProps(data?.EmailMessages);
  const nodes = data?.EmailMessages.nodes;

  const emptyFilters = useMemo(() => {
    if (typeof filter.query === 'string') {
      return false;
    }
    if (filter.environment !== 'ALL') {
      return false;
    }
    if (filter.timePeriod !== 'ALL') {
      return false;
    }
    if (filter.eventType !== 'ALL') {
      return false;
    }
    if (filter.sourceEmailMessagesBatchId) {
      return false;
    }
    return true;
  }, [filter]);

  const resetFilters = useCallback(() => {
    setFilter({
      environment: EmailMessageEnvironmentFilter.All,
      timePeriod: EmailMessageTimePeriodFilter.All,
      eventType: EmailMessageEventTypeFilter.All,
      query: null,
      sourceEmailMessagesBatchId: null,
    });
  }, [setFilter]);

  const renderBody = () => {
    if (!nodes) {
      return <TableLoader />;
    }
    if (nodes.length === 0) {
      return (
        <EmptyResultsMessage
          emptyMessageTutorial={emptyMessageTutorial}
          emptyFilters={emptyFilters}
          resetFilters={resetFilters}
        />
      );
    }
    return (
      <WpDataGrid
        columns={calculatedColumns}
        nodes={nodes}
        onRowClick={(row) => {
          const modelIds = nodes?.map((n) => n.id) ?? [];
          navigator.showEmailMessage(row.id, { modelIds });
        }}
      />
    );
  };

  return (
    <Stack spacing={1.5}>
      <TitleBar filter={filter} setFilter={setFilter} title={title} />
      <FiltersBar filter={filter} setFilter={setFilter} />
      <Paper>
        <Header
          pagination={pagination}
          title={
            <TableTitleBar
              filter={filter}
              setFilter={setFilter}
              emptyFilters={emptyFilters}
              resetFilters={resetFilters}
            />
          }
        />
        {renderBody()}
      </Paper>
    </Stack>
  );
}
